/* eslint-disable react-refresh/only-export-components */
import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps } from "react"

import { cn } from "cn"

// Styles des internen SVG — nicht von außen überschreibbar, `className`
// landet auf dem Wrapper.
const spinnerVariants = cva("shrink-0 animate-spin", {
  variants: {
    size: {
      sm: "size-5.5",
      md: "size-7",
      lg: "size-16",
    },
    // Track = voller Ring, Indicator = rotierender Bogen.
    variant: {
      white:
        "[&_[data-slot=spinner-indicator]]:stroke-neutrals-v2-500 [&_[data-slot=spinner-track]]:stroke-neutrals-v2-100",
      "light-grey":
        "[&_[data-slot=spinner-indicator]]:stroke-neutrals-v2-500 [&_[data-slot=spinner-track]]:stroke-neutrals-v2-200",
      "dark-grey":
        "[&_[data-slot=spinner-indicator]]:stroke-neutrals-v2-600 [&_[data-slot=spinner-track]]:stroke-neutrals-v2-300",
    },
  },
  defaultVariants: {
    size: "sm",
    variant: "white",
  },
})

export type SpinnerSize = NonNullable<
  VariantProps<typeof spinnerVariants>["size"]
>

export type SpinnerVariant = NonNullable<
  VariantProps<typeof spinnerVariants>["variant"]
>

// Normierte Zeichenfläche — die tatsächliche Größe kommt allein aus der
// `size-*`-Klasse, das SVG skaliert mit.
const VIEWBOX = 100
const CENTER = VIEWBOX / 2

// Figma zeichnet den Ring als Ellipsen-Segment mit `arcData.innerRadius`
// (Innenradius als Anteil des Außenradius) statt mit einer Strichstärke.
const INNER_RADIUS: Record<SpinnerSize, number> = {
  sm: 0.6,
  md: 0.6,
  lg: 0.67,
}

// Der Bogen hat in Figma runde Enden: Die Startkappe ist oben zentriert,
// das Ende reicht inklusive Rundung bis 108°.
const INDICATOR_END_DEG = 108

export type SpinnerProps = Omit<ComponentProps<"div">, "children"> &
  VariantProps<typeof spinnerVariants> & {
    /**
     * Ladetext unter dem Spinner (Figma „Spinner Text", z. B.
     * „Wird geladen ..."). Ohne Text wird nur der Spinner gerendert.
     */
    children?: string
  }

/**
 * Figma „Spinner" und „Spinner Text" in einer Komponente. Das SVG ist
 * intern — Anpassungen (`className`, native Props) wirken immer auf den
 * Wrapper, also auf Spinner und Text gemeinsam.
 */
function Spinner({
  className,
  size = "sm",
  variant = "white",
  children,
  "aria-label": ariaLabel,
  ...props
}: SpinnerProps) {
  const stroke = CENTER * (1 - INNER_RADIUS[size ?? "sm"])
  const radius = CENTER - stroke / 2
  // Die runde Kappe ragt um `stroke / 2` über das Dash-Ende hinaus.
  const capDeg = (stroke / 2 / radius) * (180 / Math.PI)
  const indicatorLength = ((INDICATOR_END_DEG - capDeg) / 360) * 100

  return (
    <div
      data-slot="spinner"
      data-size={size}
      data-variant={variant}
      role="status"
      // Mit Text wird dieser vorgelesen, ohne Text braucht es ein Label.
      aria-label={ariaLabel ?? (children ? undefined : "Wird geladen")}
      className={cn("inline-flex flex-col items-center gap-4", className)}
      {...props}
    >
      <svg
        data-slot="spinner-icon"
        aria-hidden
        viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
        fill="none"
        className={spinnerVariants({ size, variant })}
      >
        <circle
          data-slot="spinner-track"
          cx={CENTER}
          cy={CENTER}
          r={radius}
          strokeWidth={stroke}
        />
        <circle
          data-slot="spinner-indicator"
          cx={CENTER}
          cy={CENTER}
          r={radius}
          strokeWidth={stroke}
          strokeLinecap="round"
          pathLength={100}
          strokeDasharray={`${indicatorLength} 100`}
          transform={`rotate(-90 ${CENTER} ${CENTER})`}
        />
      </svg>
      {children && (
        <p
          data-slot="spinner-text"
          className="text-center text-sm text-labels-primary"
        >
          {children}
        </p>
      )}
    </div>
  )
}

export { Spinner, spinnerVariants }
